<script setup lang="ts">
import Heatmap from 'visual-heatmap';
import { useTemplateRef, onMounted, ref, watch, nextTick } from "vue";
import { useResizeObserver } from '@vueuse/core';
import type { HeatmapConfig, Point } from "visual-heatmap/dist/types/types";
import { gradient } from './brandLayout';
import type { HeatmapRenderer } from "visual-heatmap/dist/types/heatmap";
import { Dialog, DialogHeader, DialogScrollContent } from "@/components/ui/dialog";

interface BackgroundImage {
  url: string;
  width: number;
  height: number;
}

interface Plan {
  backgroundImage: BackgroundImage;
}

const props = defineProps({
  plan: {
    type: Object as () => Plan,
    required: true
  },
  metrics: {
    type: Array as () => Point[],
    default: () => []
  }
});

const canvas = useTemplateRef<HTMLDivElement>('canvas');
const canvasZoom = useTemplateRef<HTMLDivElement>('canvasZoom');
const HeatmapInstance = ref<HeatmapRenderer | null>(null);
const HeatmapZoomInstance = ref<HeatmapRenderer | null>(null);
const showModal = ref(false);

/**
 * Calculate zoom level for the heatmap based on canvas dimensions
 */
const getZoom = (canvasHeight: number, canvasWidth: number): number => {
  return props.plan.backgroundImage.width / canvasWidth;
};

/**
 * Calculate translation offsets for centering the heatmap
 */
const getTranslate = (canvasHeight: number, canvasWidth: number): [number, number] => {
  return [
    canvasWidth / 2 - props.plan.backgroundImage.width / 2,
    canvasHeight / 2 - props.plan.backgroundImage.height / 2,
  ];
};

/**
 * Initialize and render the heatmap instance
 */
const initializeHeatmap = (element: HTMLDivElement, metrics: Point[], instanceRef: typeof HeatmapInstance) => {
  const canvasHeight = element.getBoundingClientRect().height;
  const canvasWidth = element.getBoundingClientRect().width;

  const heatmap = Heatmap(element, {
    size: 100.0,
    min: 0,
    zoom: getZoom(canvasHeight, canvasWidth),
    translate: getTranslate(canvasHeight, canvasWidth),
    intensity: 1.0,
    opacity: 0.6,
    backgroundImage: {
      url: props.plan.backgroundImage.url,
      width: props.plan.backgroundImage.width,
      height: props.plan.backgroundImage.height,
      x: 0,
      y: 0,
    },
    gradient,
  } as HeatmapConfig);

  heatmap.render();
  heatmap.renderData(metrics);
  instanceRef.value = heatmap;
};

/**
 * Resize and update the heatmap instance
 */
const resizeHeatmap = (
  entries: ResizeObserverEntry[],
  instanceRef: typeof HeatmapInstance
) => {
  if (!instanceRef.value) return;
  const { height, width } = entries[0].contentRect;
  instanceRef.value.resize();
  instanceRef.value.setZoom(getZoom(height, width));
  instanceRef.value.setTranslate(getTranslate(height, width));
};

// Main canvas heatmap initialization
onMounted(() => {
  if (canvas.value) {
    initializeHeatmap(canvas.value, props.metrics as Point[], HeatmapInstance);
  }
});

// Main canvas resize observer
useResizeObserver(canvas, (entries) => resizeHeatmap(entries, HeatmapInstance));

// Modal canvas heatmap initialization
watch(
  () => showModal.value,
  (isOpen) => {
    nextTick(() => {
      if (canvasZoom.value && isOpen) {
        initializeHeatmap(canvasZoom.value, props.metrics as Point[], HeatmapZoomInstance);
      }
    })
  }
)

// Modal canvas resize observer
useResizeObserver(canvasZoom, (entries) => resizeHeatmap(entries, HeatmapZoomInstance));

</script>

<template>
  <div class="w-100" style="cursor: zoom-in" @click="showModal = true">
    <div ref="canvas" class="canvas" style="background: white;"></div>
  </div>

  <Dialog :open="showModal" @update:open="showModal = false">
    <DialogScrollContent size="xl">
      <DialogHeader>
      </DialogHeader>
      <div class="w-100">
        <div ref="canvasZoom" class="canvas" style="background: white;"></div>
      </div>
    </DialogScrollContent>
  </Dialog>
</template>

<style scoped>
.canvas {
  width: 100%;
  height: auto;
  aspect-ratio: 1 / 1;
}
</style>
